import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import { fetchProjectsApi } from "../../../services/api/boq/projectService";
import { getVendors } from "../../../services/api/boq/VendorService";
import { fetchBoqByProject, fetchBoqById } from "../../../services/api/boq/boqService";
import { getChecklists } from "../../../services/api/qa/checklistService";
import { createInspection } from "../../../services/api/qa/inspectionService";
import toast from "react-hot-toast";

function InspectionCreate() {
    const navigate = useNavigate();

    const [projects, setProjects] = useState([]);
    const [vendors, setVendors] = useState([]);
    const [boqHeaders, setBoqHeaders] = useState([]);
    const [boqItems, setBoqItems] = useState([]);
    const [checklists, setChecklists] = useState([]);

    const [loading, setLoading] = useState(false);
    const [boqHeaderLoading, setBoqHeaderLoading] = useState(false);
    const [boqItemLoading, setBoqItemLoading] = useState(false);

    const [inspectionData, setInspectionData] = useState({
        project_id: "",
        vendor_id: "",
        boq_id: "",
        inspection_date: "",
        location: "",
        remarks: "",
        inspected_by: 1, // replace with logged in user
        selectedBoqItems: [], // multiple selection
    });

    // ---------------- LOAD INITIAL DATA ----------------
    useEffect(() => {
        loadInitialData();
    }, []);

    const fetchVendors = async () => {
        let page = 1;
        let allVendors = [];
        let lastPage = 1;

        try {
            do {
                const res = await getVendors(page);

                const data = res?.data?.data;
                const vendors = data?.data ?? [];

                allVendors = [...allVendors, ...vendors];

                lastPage = data?.last_page;
                page++;
            } while (page <= lastPage);

            return allVendors; // ✅ IMPORTANT
        } catch (err) {
            console.error("Error fetching vendors:", err);
            return [];
        }
    };

    const loadInitialData = async () => {
        try {
            const [projectRes, vendorRes, checklistRes] = await Promise.all([
                fetchProjectsApi(),
                fetchVendors(),
                getChecklists(),
            ]);

            setProjects(projectRes?.data?.data?.data ?? []);
            setVendors(vendorRes ??  []);
            setChecklists(checklistRes ?? []);
        } catch (error) {
            console.error("Error loading data", error);
        }
    };

    // ---------------- PROJECT CHANGE ----------------
    const handleProjectChange = async (projectId) => {
        setInspectionData((prev) => ({
            ...prev,
            project_id: projectId,
            boq_id: "",
            selectedBoqItems: [],
        }));

        if (!projectId) return;

        setBoqHeaderLoading(true);

        try {
            const res = await fetchBoqByProject(projectId);
            setBoqHeaders(res ?? []);
        } catch (error) {
            console.error("Error loading BOQ headers", error);
        } finally {
            setBoqHeaderLoading(false);
        }
    };

    // ---------------- BOQ HEADER CHANGE ----------------
    const handleBoqChange = async (boqId) => {
        setInspectionData((prev) => ({
            ...prev,
            boq_id: boqId,
            selectedBoqItems: [],
        }));

        if (!boqId) return;

        setBoqItemLoading(true);

        try {
            const res = await fetchBoqById(boqId);
            const items = res?.data?.items ?? [];
            setBoqItems(items);
        } catch (error) {
            console.error("Error loading BOQ items", error);
        } finally {
            setBoqItemLoading(false);
        }
    };

    // ---------------- HANDLE BOQ ITEM SELECTION ----------------
    const toggleBoqItem = (itemId) => {
        setInspectionData((prev) => {
            const selected = prev.selectedBoqItems.includes(itemId)
                ? prev.selectedBoqItems.filter((id) => id !== itemId)
                : [...prev.selectedBoqItems, itemId];

            return { ...prev, selectedBoqItems: selected };
        });
    };

    // ---------------- SUBMIT ----------------
    const handleSubmit = async (e) => {
        e.preventDefault();

        if (inspectionData.selectedBoqItems.length === 0) {
            toast.error("Please select at least one BOQ item");
            return;
        }

        setLoading(true);

        try {
            const payload = {
                inspections: inspectionData.selectedBoqItems.map((boq_item_id) => ({
                    project_id: inspectionData.project_id,
                    vendor_id: inspectionData.vendor_id,
                    boq_item_id,
                    checklist_id: inspectionData.checklist_id,  // <--- Add this
                    inspection_date: inspectionData.inspection_date,
                    location: inspectionData.location,
                    remarks: inspectionData.remarks,
                    inspected_by: inspectionData.inspected_by,
                })),
            };

            const res = await createInspection(payload);
            toast.success(res.message || "Inspections Created Successfully");
            navigate("/inspections");
        } catch (error) {
            console.error("Create inspection error", error);
            toast.error("Failed to create inspections");
        } finally {
            setLoading(false);
        }
    };

    // ---------------- UI ----------------
    return (
        <div className="p-6">
            <h2 className="text-xl font-semibold mb-4">Create Inspection</h2>

            <form onSubmit={handleSubmit} className="bg-white p-6 rounded shadow space-y-4">
                {/* Date */}
                <div>
                    <label className="block text-sm font-medium mb-1">Inspection Date</label>
                    <input
                        type="date"
                        required
                        className="border p-2 rounded w-full"
                        value={inspectionData.inspection_date}
                        onChange={(e) =>
                            setInspectionData({ ...inspectionData, inspection_date: e.target.value })
                        }
                    />
                </div>

                {/* Vendor */}
                <div>
                    <label className="block text-sm font-medium mb-1">Vendor</label>
                    <select
                        required
                        className="border p-2 rounded w-full"
                        value={inspectionData.vendor_id}
                        onChange={(e) =>
                            setInspectionData({ ...inspectionData, vendor_id: e.target.value })
                        }
                    >
                        <option value="">Select Vendor</option>
                        {vendors.map((vendor) => (
                            <option key={vendor.id} value={vendor.id}>
                                {vendor.name}
                            </option>
                        ))}
                    </select>
                </div>

                {/* Checklist */}
                <div>
                    <label className="block text-sm font-medium mb-1">Checklist</label>
                    <select
                        required
                        className="border p-2 rounded w-full"
                        value={inspectionData.checklist_id}
                        onChange={(e) =>
                            setInspectionData({ ...inspectionData, checklist_id: e.target.value })
                        }
                    >
                        <option value="">Select Checklist</option>
                        {checklists.map((checklist) => (
                            <option key={checklist.id} value={checklist.id}>
                                {checklist.name}
                            </option>
                        ))}
                    </select>
                </div>

                {/* Project */}
                <div>
                    <label className="block text-sm font-medium mb-1">Project</label>
                    <select
                        required
                        className="border p-2 rounded w-full"
                        value={inspectionData.project_id}
                        onChange={(e) => handleProjectChange(e.target.value)}
                    >
                        <option value="">Select Project</option>
                        {projects.map((project) => (
                            <option key={project.id} value={project.id}>
                                {project.project_name}
                            </option>
                        ))}
                    </select>
                </div>

                {/* BOQ Header */}
                {boqHeaderLoading && <p className="text-blue-600">Loading BOQ Headers...</p>}
                {boqHeaders.length > 0 && (
                    <div>
                        <label className="block text-sm font-medium mb-1">BOQ Header</label>
                        <select
                            required
                            className="border p-2 rounded w-full"
                            value={inspectionData.boq_id}
                            onChange={(e) => handleBoqChange(e.target.value)}
                        >
                            <option value="">Select BOQ Header</option>
                            {boqHeaders.map((boq) => (
                                <option key={boq.id} value={boq.id}>
                                    {boq.boq_name}
                                </option>
                            ))}
                        </select>
                    </div>
                )}

                {/* BOQ Items (multiple selection) */}
                {boqItemLoading && <p className="text-blue-600">Loading BOQ Items...</p>}
                {boqItems.length > 0 && (
                    <div>
                        <label className="block text-sm font-medium mb-1">BOQ Items</label>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                            {boqItems.map((item) => (
                                <label
                                    key={item.id}
                                    className="flex items-center gap-2 border p-2 rounded cursor-pointer"
                                >
                                    <input
                                        type="checkbox"
                                        checked={inspectionData.selectedBoqItems.includes(item.id)}
                                        onChange={() => toggleBoqItem(item.id)}
                                    />
                                    {item.item_name}
                                </label>
                            ))}
                        </div>
                    </div>
                )}

                {/* Location */}
                <div>
                    <label className="block text-sm font-medium mb-1">Location</label>
                    <input
                        type="text"
                        required
                        className="border p-2 rounded w-full"
                        value={inspectionData.location}
                        onChange={(e) =>
                            setInspectionData({ ...inspectionData, location: e.target.value })
                        }
                    />
                </div>

                {/* Remarks */}
                <div>
                    <label className="block text-sm font-medium mb-1">Remarks</label>
                    <textarea
                        className="border p-2 rounded w-full"
                        value={inspectionData.remarks}
                        onChange={(e) =>
                            setInspectionData({ ...inspectionData, remarks: e.target.value })
                        }
                    />
                </div>

                {/* Buttons */}
                <div className="flex justify-end gap-3">
                    <Link to="/inspections" className="border px-4 py-2 rounded">
                        Back
                    </Link>
                    <button
                        type="submit"
                        disabled={loading}
                        className="bg-blue-600 text-white px-4 py-2 rounded"
                    >
                        {loading ? "Creating..." : "Create Inspection(s)"}
                    </button>
                </div>
            </form>
        </div>
    );
}

export default InspectionCreate;